import {
    FC,
    PropsWithChildren,
    useEffect,
    useState,
    useCallback,
    useContext,
    useMemo,
} from "react";
import useEmblaCarousel from "embla-carousel-react";
import Fade from "embla-carousel-fade";
import AutoScroll, { AutoScrollOptionsType } from "embla-carousel-auto-scroll";
import { EmblaCarouselType, EmblaOptionsType } from "embla-carousel";
import classNames from "classnames";
import { ConfigContextV2 } from "contexts";
import Autoplay, { AutoplayOptionsType } from "embla-carousel-autoplay";
import ClassNames from "embla-carousel-class-names";
import Button from "../button/Button";
import styles from "./carousel.module.sass";

export type CarouselProps = {
    options?: EmblaOptionsType;
    arrows?: "center" | "top" | "custom";
    autoplay?: AutoplayOptionsType;
    autoscroll?: AutoScrollOptionsType;
    fade?: boolean;
    classnames?: boolean;
    wrapperClassName?: string;
    showDots?: boolean;
    initialScrollIndex?: number;
    onInitCallback?: (currentSlide: number) => void;
    afterScrollCallback?: (currentSlide: number) => void;
    onSelectCallback?: (currentSlide: number) => void;
};

export const CarouselSlide: FC<PropsWithChildren> = ({ children }) => (
    <div className={classNames(styles.slide, "carousel-slide")}>{children}</div>
);

export type CarouselForwardedRef = {
    emblaApi: EmblaCarouselType | undefined;
};

export const Carousel: FC<CarouselProps & PropsWithChildren> = ({
    children,
    options,
    arrows,
    autoplay,
    autoscroll,
    fade,
    classnames,
    wrapperClassName,
    showDots,
    initialScrollIndex,
    onInitCallback,
    afterScrollCallback,
    onSelectCallback,
}) => {
    const { siteDirection } = useContext(ConfigContextV2);

    const carouselPlugins = useMemo(() => {
        const result = [];
        if (autoplay) {
            const sanitizedDelay =
                typeof autoplay.delay === "number" && autoplay.delay > 0 ? autoplay.delay : 3000;

            result.push(Autoplay({ ...autoplay, delay: sanitizedDelay, stopOnInteraction: false }));
        }
        if (fade) {
            result.push(Fade());
        }
        if (classnames) {
            result.push(ClassNames());
        }
        if (autoscroll) {
            result.push(AutoScroll({ ...autoscroll, stopOnInteraction: false }));
        }
        return result;
    }, [autoplay, fade, classnames, autoscroll]);

    const sanitizedOptions = useMemo(() => {
        const duration =
            typeof options?.duration === "number" &&
            options.duration >= 20 &&
            options.duration <= 60
                ? options.duration
                : 20;

        return { ...options, duration };
    }, [options]);
    const [emblaRef, emblaApi] = useEmblaCarousel(
        {
            align: "start",
            direction: siteDirection === "rtl" ? "rtl" : "ltr",
            ...sanitizedOptions,
        },
        carouselPlugins,
    );
    const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
    const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

    const [selectedIndex, setSelectedIndex] = useState(0);
    const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

    const onPrevClick = useCallback(() => {
        if (emblaApi) emblaApi.scrollPrev();
    }, [emblaApi]);

    const onNextClick = useCallback(() => {
        if (emblaApi) emblaApi.scrollNext();
    }, [emblaApi]);

    const onDotButtonClick = useCallback(
        (index: number) => {
            if (emblaApi) emblaApi.scrollTo(index);
        },
        [emblaApi],
    );

    useEffect(() => {
        if (!emblaApi) return;

        const updateButtonsState = (emblaApi: EmblaCarouselType) => {
            setPrevBtnDisabled(!emblaApi.canScrollPrev());
            setNextBtnDisabled(!emblaApi.canScrollNext());
        };

        const updateScrollSnaps = (emblaApi: EmblaCarouselType) => {
            if (showDots) {
                setScrollSnaps(emblaApi.scrollSnapList());
            }
        };

        const onInit = (emblaApi: EmblaCarouselType) => {
            updateScrollSnaps(emblaApi);
            onInitCallback?.(emblaApi.selectedScrollSnap());
            if (initialScrollIndex !== undefined && initialScrollIndex > 0) {
                emblaApi.scrollTo(initialScrollIndex, true);
            }
        };
        const onSettle = (emblaApi: EmblaCarouselType) => {
            if (showDots) {
                const currentIndex = emblaApi.selectedScrollSnap();
                afterScrollCallback?.(currentIndex);
            }
        };
        const onSelect = (emblaApi: EmblaCarouselType) => {
            updateButtonsState(emblaApi);

            const currentIndex = emblaApi.selectedScrollSnap();
            setSelectedIndex(currentIndex);
            onSelectCallback?.(currentIndex);
        };

        onInit(emblaApi);
        onSelect(emblaApi);

        emblaApi
            .on("reInit", onInit)
            .on("reInit", onSelect)
            .on("select", onSelect)
            .on("settle", onSettle);
        return () => {
            emblaApi.off("reInit", onInit);
            emblaApi.off("reInit", onSelect);
            emblaApi.off("select", onSelect);
            emblaApi.off("settle", onSettle);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emblaApi, onInitCallback]);

    return (
        <div
            className={classNames(styles.carousel, fade && styles.fade, wrapperClassName)}
            dir={options?.direction}>
            <div className={styles.viewport} ref={emblaRef}>
                <div className={styles.container}>{children}</div>
            </div>
            {arrows && !(prevBtnDisabled && nextBtnDisabled) && (
                <>
                    <Button
                        type="button"
                        variant="custom"
                        className={classNames(styles.prev, styles[arrows])}
                        onClick={onPrevClick}
                        disabled={prevBtnDisabled}
                    />
                    <Button
                        type="button"
                        variant="custom"
                        className={classNames(styles.next, styles[arrows])}
                        onClick={onNextClick}
                        disabled={nextBtnDisabled}
                    />
                </>
            )}
            {showDots && scrollSnaps.length > 1 && (
                <div className={styles.dots}>
                    {scrollSnaps.map((_, index) => (
                        <Button
                            key={index}
                            type="button"
                            variant="custom"
                            onClick={() => onDotButtonClick(index)}
                            className={classNames({
                                [styles.selectedDot]: index === selectedIndex,
                            })}>
                            <span
                                className={styles.dot}
                                style={
                                    index === selectedIndex
                                        ? { transitionDuration: "4s" }
                                        : undefined
                                }
                            />
                        </Button>
                    ))}
                </div>
            )}
        </div>
    );
};
